import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations/board-organization`;

export const HIGHLIGHT_COLORS = ["amber", "blue", "green", "gray", "pink", "violet"];
export const HIGHLIGHT_CELLS = new Set([
  "project_number", "project_name", "due_date", "operational_status",
  "customer_supervisor", "supervisor", "permit_status", "permit_expiration",
  "locate_status", "locate_ticket_number", "water_locate", "water_locate_notes",
  "next_scheduled_date", "recent_notes", "notes",
]);

export function useBoardOrganization(canOrganize, onMutated) {
  const [orgMap, setOrgMap] = useState({});
  const [dividers, setDividers] = useState([]);
  const [frozenSections, setFrozenSections] = useState([]);
  const [sectionOrders, setSectionOrders] = useState({});

  const reload = useCallback(async () => {
    try {
      const res = await axios.get(API);
      setOrgMap(res.data?.organization || {});
      setDividers(res.data?.dividers || []);
      setFrozenSections(res.data?.frozen_sections || []);
      setSectionOrders(res.data?.section_orders || {});
    } catch {
      // silent — read is best-effort
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const guarded = useCallback(async (label, fn) => {
    if (!canOrganize) return;
    try {
      await fn();
      await reload();
      if (onMutated) await onMutated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || `${label} failed.`);
      toast.error(msg);
    }
  }, [canOrganize, reload, onMutated]);

  // Unified move — works for a project row.
  // after = { kind: "project"|"divider"|null, id }
  const move = (projectId, sectionKey, after, expectedVersion) =>
    guarded("Move", () => axios.post(`${API}/${projectId}/move`, {
      section_key: sectionKey,
      after_project_id: after?.kind === "project" ? after.id : null,
      after_divider_id: after?.kind === "divider" ? after.id : null,
      expected_version: expectedVersion ?? null,
    }));

  const restoreSection = (sectionKey) =>
    guarded("Restore automatic order", () => axios.post(`${API}/sections/${sectionKey}/restore-automatic`, {
      expected_version: sectionOrders[sectionKey]?.version ?? 0,
    }));

  const setHighlight = (projectId, cellKey, color, sectionKey) =>
    guarded("Highlight", () => axios.post(`${API}/${projectId}/highlight`, {
      cell_key: cellKey, color, section_key: sectionKey,
    }));

  const clearAllHighlights = (projectId) =>
    guarded("Clear all highlights", () => axios.post(`${API}/${projectId}/highlight/clear-all`));

  const createDivider = (sectionKey, label, color, after) =>
    guarded("Create divider", () => axios.post(`${API}/dividers`, {
      section_key: sectionKey, label, color: color || null,
      after_project_id: after?.kind === "project" ? after.id : null,
      after_divider_id: after?.kind === "divider" ? after.id : null,
    }));

  const renameDivider = (dividerId, label, color, expectedVersion) =>
    guarded("Rename divider", () => axios.patch(`${API}/dividers/${dividerId}`, {
      label, color, expected_version: expectedVersion ?? null,
    }));

  const moveDivider = (dividerId, sectionKey, after, expectedVersion) =>
    guarded("Move divider", () => axios.post(`${API}/dividers/${dividerId}/move`, {
      section_key: sectionKey,
      after_project_id: after?.kind === "project" ? after.id : null,
      after_divider_id: after?.kind === "divider" ? after.id : null,
      expected_version: expectedVersion ?? null,
    }));

  const deleteDivider = (dividerId) =>
    guarded("Delete divider", () => axios.delete(`${API}/dividers/${dividerId}`));

  const freezeSection = (sectionKey) =>
    guarded("Freeze section", () => axios.post(`${API}/sections/${sectionKey}/freeze`));

  const unfreezeSection = (sectionKey) =>
    guarded("Unfreeze section", () => axios.post(`${API}/sections/${sectionKey}/unfreeze`));

  return {
    orgMap, dividers, frozenSections, sectionOrders, reload,
    move, restoreSection, setHighlight, clearAllHighlights,
    createDivider, renameDivider, moveDivider, deleteDivider,
    freezeSection, unfreezeSection,
  };
}
